/**
 * Recurrence-spec pre schedule engine, vrstva 3.
 * Uzavretá schéma (§2): validácia (catchUp POVINNÝ), normalizácia (defaulty + kanonické
 * poradie), specHash (deterministický, SYNCHRÓNNY sha256 kanonického JSON).
 * Čistý, deterministický, zero I/O. Dva specy s rovnakým hashom sa rozvinú rovnako.
 */

import { isoWeekday } from './civil.js';

const TZ = 'Europe/Bratislava';
const KINDS = ['one_off', 'recurring'];
const FREQS = ['daily', 'weekly', 'monthly', 'yearly'];
const OVERFLOWS = ['clamp', 'skip'];
const ADJUSTS = ['none', 'previous_working', 'next_working'];
const CATCHUPS = ['all', 'latest_only', 'skip'];
const END_TYPES = ['never', 'until', 'count'];
const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']; // ISO 1..7 → index 0..6
const WD_ORDER = Object.fromEntries(WEEKDAYS.map((w, i) => [w, i]));

const isDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isTime = (v) => v === null || (typeof v === 'string' && /^\d{2}:\d{2}$/.test(v));

// ─────────────────────────────────────────────────────────────────────────────
// validateSpec — uzavretá schéma, catchUp povinný, tz len Europe/Bratislava
// ─────────────────────────────────────────────────────────────────────────────
export function validateSpec(spec) {
  const errors = [];
  const err = (path, code, message) => errors.push({ path, code, message });

  if (!spec || typeof spec !== 'object') {
    err('', 'not_object', 'spec must be an object');
    return { ok: false, errors };
  }
  if (spec.v !== 1) err('v', 'unsupported_version', 'v must be 1');
  if (spec.tz !== TZ) err('tz', 'unsupported_tz', `tz must be ${TZ}`);
  if (!KINDS.includes(spec.kind)) err('kind', 'invalid', 'kind must be one_off or recurring');

  const a = spec.anchor;
  if (!a || typeof a !== 'object') {
    err('anchor', 'required', 'anchor is required');
  } else {
    if (!isDate(a.date)) err('anchor.date', 'invalid', 'anchor.date must be YYYY-MM-DD');
    if (!('time' in a) || !isTime(a.time)) err('anchor.time', 'invalid', 'anchor.time must be HH:MM or null');
  }

  // catchUp je POVINNÝ, bez defaultu (HARD LOCK 4) — zmeškaný termín nikdy ticho nezmizne.
  if (!CATCHUPS.includes(spec.catchUp)) err('catchUp', 'required', 'catchUp must be all, latest_only or skip');

  if (spec.kind === 'recurring') {
    if (!FREQS.includes(spec.freq)) err('freq', 'invalid', 'freq must be daily/weekly/monthly/yearly');
    if ('interval' in spec && !(Number.isInteger(spec.interval) && spec.interval >= 1)) {
      err('interval', 'invalid', 'interval must be an integer >= 1');
    }
    if ('monthdayOverflow' in spec && !OVERFLOWS.includes(spec.monthdayOverflow)) {
      err('monthdayOverflow', 'invalid', 'monthdayOverflow must be clamp or skip');
    }
    if ('businessDayAdjust' in spec && !ADJUSTS.includes(spec.businessDayAdjust)) {
      err('businessDayAdjust', 'invalid', 'businessDayAdjust must be none, previous_working or next_working');
    }
    if ('byWeekday' in spec) {
      if (!Array.isArray(spec.byWeekday) || !spec.byWeekday.every((w) => WEEKDAYS.includes(w))) {
        err('byWeekday', 'invalid', 'byWeekday must be a subset of MO..SU');
      }
    }
    if ('byMonthday' in spec) {
      const ok = Array.isArray(spec.byMonthday)
        && spec.byMonthday.every((d) => Number.isInteger(d) && ((d >= 1 && d <= 31) || d === -1));
      if (!ok) err('byMonthday', 'invalid', 'byMonthday entries must be 1..31 or -1');
    }
    if ('end' in spec) {
      if (!spec.end || !END_TYPES.includes(spec.end.type)) {
        err('end', 'invalid', 'end.type must be never, until or count');
      } else if (spec.end.type === 'until' && !isDate(spec.end.date)) {
        err('end.date', 'invalid', 'until requires a YYYY-MM-DD date');
      } else if (spec.end.type === 'count' && !(Number.isInteger(spec.end.n) && spec.end.n >= 1)) {
        err('end.n', 'invalid', 'count requires n >= 1');
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

// ─────────────────────────────────────────────────────────────────────────────
// normalizeSpec — defaulty, zoradené polia, kanonické poradie; idempotentná; hádže na neplatnom
// ─────────────────────────────────────────────────────────────────────────────
export function normalizeSpec(spec) {
  const { ok, errors } = validateSpec(spec);
  if (!ok) {
    throw new Error(`Invalid spec: ${errors.map((e) => `${e.path}: ${e.message}`).join('; ')}`);
  }

  const out = {
    v: 1,
    tz: TZ,
    kind: spec.kind,
    anchor: { date: spec.anchor.date, time: spec.anchor.time ?? null },
  };

  if (spec.kind === 'recurring') {
    out.freq = spec.freq;
    out.interval = 'interval' in spec ? spec.interval : 1;

    if (spec.freq === 'weekly') {
      const wd = Array.isArray(spec.byWeekday) && spec.byWeekday.length
        ? [...spec.byWeekday]
        : [WEEKDAYS[isoWeekday(spec.anchor.date) - 1]];
      out.byWeekday = [...new Set(wd)].sort((x, y) => WD_ORDER[x] - WD_ORDER[y]);
    }

    if (Array.isArray(spec.byMonthday) && spec.byMonthday.length) {
      out.byMonthday = [...spec.byMonthday].sort((x, y) => x - y);
    }

    out.monthdayOverflow = 'monthdayOverflow' in spec ? spec.monthdayOverflow : 'clamp';
    out.businessDayAdjust = 'businessDayAdjust' in spec ? spec.businessDayAdjust : 'none';
    out.end = spec.end ? { ...spec.end } : { type: 'never' };
  }

  out.catchUp = spec.catchUp;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// specHash — kanonický JSON → sync sha256 hex. Rovnaký hash ⇔ rovnaké rozvinutie.
// ─────────────────────────────────────────────────────────────────────────────
export function specHash(spec) {
  return sha256Hex(canonicalJson(normalizeSpec(spec)));
}

// Kanonický JSON: kľúče zoradené rekurzívne → nezávislý od poradia kľúčov na vstupe.
function canonicalJson(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(',')}}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// sync SHA-256 (pure JS, žiadny crypto.subtle — specHash je synchrónny)
// ─────────────────────────────────────────────────────────────────────────────
function sha256Hex(message) {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));

  // UTF-8 bytes
  const bytes = [];
  for (let i = 0; i < message.length; i += 1) {
    const c = message.charCodeAt(i);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
  }

  const bitLen = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i -= 1) bytes.push((bitLen / 2 ** (8 * i)) & 0xff);

  const w = new Array(64);
  for (let off = 0; off < bytes.length; off += 64) {
    for (let i = 0; i < 16; i += 1) {
      w[i] = (bytes[off + i * 4] << 24) | (bytes[off + i * 4 + 1] << 16)
        | (bytes[off + i * 4 + 2] << 8) | bytes[off + i * 4 + 3];
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
    H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
  }

  return H.map((x) => (x >>> 0).toString(16).padStart(8, '0')).join('');
}
